import dayjs from 'dayjs';
import localeDe from 'dayjs/locale/de.js';
import localeEl from 'dayjs/locale/el.js';
import localeEs from 'dayjs/locale/es.js';
import localeFr from 'dayjs/locale/fr.js';
import localeHu from 'dayjs/locale/hu.js';
import localeIt from 'dayjs/locale/it.js';
import localePl from 'dayjs/locale/pl.js';
import localePt from 'dayjs/locale/pt.js';
import localePtBr from 'dayjs/locale/pt-br.js';
import localeRu from 'dayjs/locale/ru.js';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import duration from 'dayjs/plugin/duration.js';
import isBetween from 'dayjs/plugin/isBetween.js';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter.js';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore.js';
import localeData from 'dayjs/plugin/localeData.js';
import localizedFormat from 'dayjs/plugin/localizedFormat.js';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import timezone from 'dayjs/plugin/timezone.js';
import updateLocale from 'dayjs/plugin/updateLocale.js';
import utc from 'dayjs/plugin/utc.js';
import { mapAppLocaleToDayjs, RELATIVE_TIME_BY_LOCALE } from './app-locale-map.ts';

// The legacy dayjs setup remapped `en → en-gb` so English dayjs code got
// Monday as the first day of the week. Tests written against that legacy
// behavior depend on Monday, so preserve `weekStart: 1` here. Date-format
// semantics intentionally stay on dayjs' `en` default (MM/DD/YYYY) to match
// moment's behavior — older tests (e.g. region.utils.test.ts) rely on it.
const EN_LOCALE_OVERRIDE = {
  weekStart: 1
};

const registerDayjsPlugins = () => {
  dayjs.extend(customParseFormat);
  dayjs.extend(relativeTime);
  dayjs.extend(localizedFormat);
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.extend(isSameOrBefore);
  dayjs.extend(isSameOrAfter);
  dayjs.extend(isBetween);
  dayjs.extend(updateLocale);
  dayjs.extend(duration);
  dayjs.extend(localeData);
};

const originalLocaleFn = dayjs.locale.bind(dayjs);
let localeGuardEnabled = false;
let initialized = false;

export const setDayjsLocale = (appLocale: string) => {
  if (!initialized) {
    initializeDayjs(appLocale);
    return;
  }
  const dayjsLocale = mapAppLocaleToDayjs(appLocale);
  localeGuardEnabled = false;
  try {
    originalLocaleFn(dayjsLocale);
  } finally {
    localeGuardEnabled = true;
  }
};

const guardedLocale = (...args: unknown[]) => {
  if (args.length > 0 && args[0] !== undefined) {
    if (localeGuardEnabled) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('dayjs.locale(...) is guarded. Use setDayjsLocale() from @protector/utils/dayjs instead.');
      }
      return originalLocaleFn();
    }
    return originalLocaleFn(...(args as Parameters<typeof originalLocaleFn>));
  }
  return originalLocaleFn();
};

// The first call seeds the initial locale and installs the guard. Subsequent
// calls only switch the active locale via setDayjsLocale — they do not re-run
// plugin registration or reinstall the guard.
export const initializeDayjs = (initialAppLocale: string) => {
  if (initialized) {
    setDayjsLocale(initialAppLocale);
    return;
  }
  initialized = true;

  registerDayjsPlugins();

  originalLocaleFn('de', localeDe);
  originalLocaleFn('el', localeEl);
  originalLocaleFn('es', localeEs);
  originalLocaleFn('fr', localeFr);
  originalLocaleFn('hu', localeHu);
  originalLocaleFn('it', localeIt);
  originalLocaleFn('pl', localePl);
  originalLocaleFn('pt-br', localePtBr);
  originalLocaleFn('pt-pt', localePt);
  originalLocaleFn('ru', localeRu);

  for (const [locale, relativeTimeConfig] of Object.entries(RELATIVE_TIME_BY_LOCALE)) {
    dayjs.updateLocale(locale, { relativeTime: relativeTimeConfig });
  }

  dayjs.updateLocale('en', EN_LOCALE_OVERRIDE);

  originalLocaleFn(mapAppLocaleToDayjs(initialAppLocale));

  Object.defineProperty(dayjs, 'locale', {
    value: guardedLocale,
    writable: false,
    configurable: false
  });

  localeGuardEnabled = true;
};
